
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Calendar } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CustomerSelect } from "./CustomerSelect";
import { PaymentMethodSelect } from "./PaymentMethodSelect";
import { PaymentStatusSelect } from "./PaymentStatusSelect";
import { Button } from "@/components/ui/button";
import { Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useState } from "react";

interface TransactionFormFieldsProps {
  transactionType: string;
  selectedCustomer: any;
  setSelectedCustomer: (customer: any) => void;
  customerName: string;
  setCustomerName: (name: string) => void;
  amount: string;
  setAmount: (amount: string) => void;
  date: string;
  setDate: (date: string) => void;
  description: string;
  setDescription: (description: string) => void;
  name?: string;
  setName?: (name: string) => void;
  category?: string;
  setCategory?: (category: string) => void;
  paymentMethod: string;
  setPaymentMethod: (method: string) => void;
  paymentStatus: string;
  setPaymentStatus: (status: string) => void;
  onDelete?: () => void;
}

export function TransactionFormFields({
  transactionType,
  selectedCustomer,
  setSelectedCustomer,
  customerName,
  setCustomerName,
  amount,
  setAmount,
  date,
  setDate,
  description,
  setDescription,
  name,
  setName,
  category,
  setCategory,
  paymentMethod,
  setPaymentMethod,
  paymentStatus,
  setPaymentStatus,
  onDelete
}: TransactionFormFieldsProps) {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  if (transactionType !== "despesas") {
    return (
      <>
        <div className="space-y-2 mb-6">
          <CustomerSelect 
            selectedCustomer={selectedCustomer}
            setSelectedCustomer={setSelectedCustomer}
            customerName={customerName}
            setCustomerName={setCustomerName}
          />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label>Valor</Label>
            <Input 
              type="number" 
              placeholder="R$ 0,00" 
              className="w-full bg-[#1A1F2C] text-white border-gray-700 focus:border-[#0073B7] focus:ring-[#0073B7]"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Data</Label>
            <div className="relative">
              <Input 
                type="date" 
                className="w-full bg-[#1A1F2C] text-white border-gray-700 focus:border-[#0073B7] focus:ring-[#0073B7]"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
              <Calendar className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
            </div>
          </div>
        </div>

        <div className="space-y-2 my-6">
          <Label>Descrição</Label>
          <Input 
            placeholder="Descrição" 
            className="w-full bg-[#1A1F2C] text-white border-gray-700 focus:border-[#0073B7] focus:ring-[#0073B7]"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        {transactionType === "saida" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="space-y-2">
              <Label>Nome</Label>
              <Input 
                placeholder="Nome da saída" 
                className="w-full bg-[#1A1F2C] text-white border-gray-700 focus:border-[#0073B7] focus:ring-[#0073B7]"
                value={name}
                onChange={(e) => setName && setName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Tipo</Label>
              <Select value={category} onValueChange={(value) => setCategory && setCategory(value)}>
                <SelectTrigger className="w-full bg-[#1A1F2C] text-white border-gray-700">
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="material">Material</SelectItem>
                  <SelectItem value="servico">Serviço</SelectItem>
                  <SelectItem value="outros">Outros</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        <div className="space-y-4 mb-6">
          <PaymentMethodSelect 
            paymentMethod={paymentMethod} 
            setPaymentMethod={setPaymentMethod} 
          />
          
          <PaymentStatusSelect 
            paymentStatus={paymentStatus} 
            setPaymentStatus={setPaymentStatus} 
          />
        </div>

        {onDelete && (
          <div className="border-t border-gray-700 pt-4 mt-6">
            <Button 
              variant="outline" 
              className="w-full border-[#E60026] text-[#E60026] hover:bg-[#E60026]/10 hover:text-[#E60026]"
              onClick={() => setShowDeleteDialog(true)}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Excluir Transação
            </Button>
          </div>
        )}

        {onDelete && (
          <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
            <DialogContent className="bg-[#1A1F2C] border-gray-700 text-white">
              <DialogHeader>
                <DialogTitle className="text-xl font-bold">Confirmar Exclusão</DialogTitle>
                <DialogDescription className="text-gray-300">
                  Tem certeza que deseja excluir esta transação? Esta ação não pode ser desfeita.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button 
                  variant="outline" 
                  className="border-gray-600 text-white hover:bg-gray-700"
                  onClick={() => setShowDeleteDialog(false)}
                >
                  Cancelar
                </Button>
                <Button 
                  variant="destructive" 
                  className="bg-[#E60026] hover:bg-[#E60026]/90 text-white"
                  onClick={() => {
                    onDelete();
                    setShowDeleteDialog(false);
                  }}
                >
                  Excluir
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </>
    );
  }
  
  return null;
}
